import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BadgeCheck,
  CheckCircle2,
  FileClock,
  FilePlus2,
  ListFilter,
  RefreshCw,
  ScrollText,
  Send,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { EmptyState, Loading, PageHeader, inr } from "../../components/shared";

const QUOTE_STATUSES = [
  "DRAFT",
  "ISSUED",
  "APPROVED",
  "ACCEPTED",
  "REJECTED",
  "EXPIRED",
  "SUPERSEDED",
];

const PERMISSION = (user, module, action = "view") =>
  user?.role === "super_admin" || Boolean(user?.permissions?.[module]?.[action]);

const isoDate = (offsetDays = 0) => {
  const value = new Date();
  value.setDate(value.getDate() + offsetDays);
  return value.toISOString().slice(0, 10);
};

export default function AdminCommercialQuotes() {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [societies, setSocieties] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [selected, setSelected] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("quotes");
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [reason, setReason] = useState("");
  const [acceptance, setAcceptance] = useState({
    accepted_by_name: "",
    method: "Signed commercial acceptance",
    note: "",
    reference: "",
  });
  const [form, setForm] = useState({
    customer_email: "",
    customer_name: "",
    customer_phone: "",
    customer_role: "Secretary / authorized representative",
    duration_months: "12",
    expires_on: isoDate(14),
    resident_portal: false,
    society_id: "",
    units: "100",
  });

  const canCreate = PERMISSION(user, "commercial_quotes", "create");
  const canEdit = PERMISSION(user, "commercial_quotes", "edit");
  const canApprove = PERMISSION(user, "commercial_quotes", "approve");
  const canAccept = PERMISSION(user, "commercial_quote_acceptance", "create");
  const canContracts = PERMISSION(user, "commercial_contracts");
  const canInvoice = (
    PERMISSION(user, "commercial_invoicing", "create")
    && PERMISSION(user, "company_accounting", "create")
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const calls = [api.get("/admin/commercial-quotes")];
      if (canContracts) calls.push(api.get("/admin/commercial-quotes/contracts"));
      if (canCreate) calls.push(api.get("/admin/commercial-quotes/societies"));
      const [quoteResponse, contractResponse, societyResponse] = await Promise.all(calls);
      setQuotes(quoteResponse.data);
      setContracts(contractResponse?.data || []);
      setSocieties(societyResponse?.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load commercial quotations");
    } finally {
      setLoading(false);
    }
  }, [canContracts, canCreate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!selectedId) {
      setSelected(null);
      setTimeline([]);
      return;
    }
    Promise.all([
      api.get(`/admin/commercial-quotes/${selectedId}`),
      api.get(`/admin/commercial-quotes/${selectedId}/timeline`),
    ])
      .then(([quoteResponse, timelineResponse]) => {
        setSelected(quoteResponse.data);
        setTimeline(timelineResponse.data);
        setReason("");
        setAcceptance({
          accepted_by_name: quoteResponse.data.customer_contact?.name || "",
          method: "Signed commercial acceptance",
          note: "",
          reference: "",
        });
      })
      .catch((error) => toast.error(error.response?.data?.detail || "Could not load quotation"));
  }, [selectedId]);

  const visibleQuotes = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return quotes.filter((quote) => {
      const matchesStatus = !status || quote.status === status;
      const matchesSearch = !needle || [
        quote.quotation_number,
        quote.society_name,
        quote.customer_contact?.name,
      ].some((value) => String(value || "").toLowerCase().includes(needle));
      return matchesStatus && matchesSearch;
    });
  }, [quotes, search, status]);

  const selectedSociety = societies.find((society) => society.society_id === form.society_id);

  const updateForm = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const previewQuote = async () => {
    try {
      const response = await api.post("/admin/commercial-quotes/preview", {
        duration_months: Number(form.duration_months),
        resident_portal: form.resident_portal,
        units: Number(form.units),
      });
      setPreview(response.data);
    } catch (error) {
      setPreview(null);
      toast.error(error.response?.data?.detail || "Could not calculate the commercial quote");
    }
  };

  const createQuote = async () => {
    setSaving(true);
    try {
      const response = await api.post("/admin/commercial-quotes", {
        customer_contact: {
          email: form.customer_email,
          name: form.customer_name,
          phone: form.customer_phone,
          role: form.customer_role,
        },
        duration_months: Number(form.duration_months),
        expires_on: form.expires_on,
        resident_portal: form.resident_portal,
        society_id: form.society_id,
        units: Number(form.units),
      });
      toast.success("Commercial quotation draft created");
      await load();
      setSelectedId(response.data.quotation_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create quotation");
    } finally {
      setSaving(false);
    }
  };

  const transition = async (action, body = {}) => {
    if (!selected) return;
    setSaving(true);
    try {
      const response = await api.post(
        `/admin/commercial-quotes/${selected.quotation_id}/${action}`,
        body,
      );
      const quote = response.data.quote || response.data;
      setSelected(quote);
      if (response.data.contract) {
        setContracts((rows) => [response.data.contract, ...rows.filter((row) => (
          row.contract_id !== response.data.contract.contract_id
        ))]);
      }
      await load();
      const timelineResponse = await api.get(
        `/admin/commercial-quotes/${selected.quotation_id}/timeline`,
      );
      setTimeline(timelineResponse.data);
      toast.success(`Quotation ${action}d`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Could not ${action} quotation`);
    } finally {
      setSaving(false);
    }
  };

  const generateInvoice = async (contractId) => {
    setSaving(true);
    try {
      const response = await api.post(`/admin/commercial-quotes/contracts/${contractId}/invoice`);
      toast.success(
        response.data.idempotent
          ? "Existing Company Finance invoice linked"
          : "Company Finance invoice generated",
      );
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not generate Company Finance invoice");
      await load();
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <div data-testid="commercial-quotes-page">
      <PageHeader
        title="Commercial Quotations"
        subtitle={
          "Controlled Budget Launch quotes and accepted contract terms. " +
          "No invoice or payment is posted here."
        }
      >
        <Button
          data-testid="commercial-quotes-refresh-button"
          onClick={load}
          type="button"
          variant="outline"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </PageHeader>

      <Tabs onValueChange={setActiveTab} value={activeTab}>
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger data-testid="commercial-quotes-list-tab" value="quotes">
            Quotations
          </TabsTrigger>
          {canCreate && (
            <TabsTrigger data-testid="commercial-quotes-create-tab" value="create">
              Create quote
            </TabsTrigger>
          )}
          {canContracts && (
            <TabsTrigger data-testid="commercial-contracts-tab" value="contracts">
              Accepted contracts
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="quotes">
          <div className="mt-5 grid gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
            <section className="min-w-0">
              <div className="mb-4 flex flex-wrap gap-3">
                <Input
                  className="max-w-sm"
                  data-testid="commercial-quotes-search-input"
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search number, society or contact"
                  value={search}
                />
                <select
                  className="h-10 border bg-background px-3 text-sm"
                  data-testid="commercial-quotes-status-filter"
                  onChange={(event) => setStatus(event.target.value)}
                  value={status}
                >
                  <option value="">All statuses</option>
                  {QUOTE_STATUSES.map((value) => <option key={value}>{value}</option>)}
                </select>
              </div>
              <QuoteTable quotes={visibleQuotes} onSelect={setSelectedId} selectedId={selectedId} />
            </section>
            <QuoteDetail
              acceptance={acceptance}
              canAccept={canAccept}
              canApprove={canApprove}
              canEdit={canEdit}
              onAccept={() => transition("accept", { customer_acceptance: acceptance })}
              onApprove={() => transition("approve", { override_reason: reason })}
              onIssue={() => transition("issue")}
              onReject={() => transition("reject", { reason })}
              onSupersede={() => transition("supersede", { reason })}
              onReasonChange={setReason}
              onAcceptanceChange={setAcceptance}
              quote={selected}
              reason={reason}
              saving={saving}
              timeline={timeline}
              user={user}
            />
          </div>
        </TabsContent>

        {canCreate && (
          <TabsContent value="create">
            <QuoteBuilder
              canCreate={canCreate}
              createQuote={createQuote}
              form={form}
              onPreview={previewQuote}
              preview={preview}
              saving={saving}
              selectedSociety={selectedSociety}
              societies={societies}
              updateForm={updateForm}
            />
          </TabsContent>
        )}

        {canContracts && (
          <TabsContent value="contracts">
            <ContractsTable
              canInvoice={canInvoice}
              contracts={contracts}
              onGenerateInvoice={generateInvoice}
              saving={saving}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function QuoteBuilder({
  canCreate,
  createQuote,
  form,
  onPreview,
  preview,
  saving,
  selectedSociety,
  societies,
  updateForm,
}) {
  return (
    <div className="mt-5 grid gap-6 xl:grid-cols-[minmax(0,1fr)_440px]">
      <section className="border bg-card p-6" data-testid="commercial-quote-builder">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 items-center justify-center bg-orange-50 text-orange-600">
            <FilePlus2 className="h-5 w-5" />
          </span>
          <div>
            <h2 className="font-display text-2xl font-black">Create a server-priced quote</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Money, discounts and GST are calculated only by the active commercial policy.
            </p>
          </div>
        </div>

        <div className="mt-7 grid gap-4 md:grid-cols-2">
          <Field label="Society">
            <select
              className="h-10 w-full border bg-background px-3 text-sm"
              data-testid="commercial-quote-society-select"
              onChange={(event) => updateForm("society_id", event.target.value)}
              value={form.society_id}
            >
              <option value="">Select an approved society</option>
              {societies.map((society) => (
                <option key={society.society_id} value={society.society_id}>
                  {society.name || society.society_id} {society.city ? `· ${society.city}` : ""}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Quote expiry">
            <Input
              data-testid="commercial-quote-expiry-input"
              onChange={(event) => updateForm("expires_on", event.target.value)}
              type="date"
              value={form.expires_on}
            />
          </Field>
          <Field label="Enrolled flats / shops">
            <Input
              data-testid="commercial-quote-units-input"
              min="1"
              onChange={(event) => updateForm("units", event.target.value)}
              type="number"
              value={form.units}
            />
          </Field>
          <Field label="Subscription duration">
            <select
              className="h-10 w-full border bg-background px-3 text-sm"
              data-testid="commercial-quote-duration-select"
              onChange={(event) => updateForm("duration_months", event.target.value)}
              value={form.duration_months}
            >
              {[3, 6, 12, 24].map((months) => (
                <option key={months} value={months}>{months} months</option>
              ))}
            </select>
          </Field>
        </div>

        <label className="mt-5 flex items-center justify-between gap-3 border p-4">
          <span>
            <strong className="block text-sm">Include Resident Portal</strong>
            <span className="text-xs text-muted-foreground">
              Quoted separately at the active rate.
            </span>
          </span>
          <Switch
            checked={form.resident_portal}
            data-testid="commercial-quote-resident-portal-toggle"
            onCheckedChange={(value) => updateForm("resident_portal", value)}
          />
        </label>

        <div className="mt-7 border-t pt-6">
          <h3 className="font-semibold">Authorized customer contact</h3>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <Field label="Name">
              <Input
                data-testid="commercial-quote-contact-name-input"
                onChange={(event) => updateForm("customer_name", event.target.value)}
                placeholder={selectedSociety?.secretary_name || "Authorized representative"}
                value={form.customer_name}
              />
            </Field>
            <Field label="Role / authority">
              <Input
                data-testid="commercial-quote-contact-role-input"
                onChange={(event) => updateForm("customer_role", event.target.value)}
                value={form.customer_role}
              />
            </Field>
            <Field label="Email">
              <Input
                data-testid="commercial-quote-contact-email-input"
                onChange={(event) => updateForm("customer_email", event.target.value)}
                type="email"
                value={form.customer_email}
              />
            </Field>
            <Field label="Phone">
              <Input
                data-testid="commercial-quote-contact-phone-input"
                onChange={(event) => updateForm("customer_phone", event.target.value)}
                placeholder={selectedSociety?.secretary_mobile || ""}
                value={form.customer_phone}
              />
            </Field>
          </div>
        </div>

        <div className="mt-7 flex flex-wrap gap-3">
          <Button
            data-testid="commercial-quote-preview-button"
            onClick={onPreview}
            type="button"
            variant="outline"
          >
            <ListFilter className="h-4 w-4" /> Preview pricing
          </Button>
          <Button
            data-testid="commercial-quote-create-button"
            disabled={!canCreate || saving}
            onClick={createQuote}
            type="button"
          >
            <FilePlus2 className="h-4 w-4" /> Create draft quote
          </Button>
        </div>
      </section>
      <QuoteSnapshot
        quote={preview}
        testId="commercial-quote-preview"
        title="Live policy preview"
      />
    </div>
  );
}

function QuoteTable({ onSelect, quotes, selectedId }) {
  if (!quotes.length) {
    return <EmptyState icon={FileClock} title="No commercial quotations match this filter" />;
  }
  return (
    <section className="overflow-x-auto border bg-card" data-testid="commercial-quotes-table">
      <table className="min-w-[840px] w-full text-sm">
        <thead className="bg-muted/60 text-left">
          <tr>
            <th className="p-3">Quote</th>
            <th className="p-3">Society</th>
            <th className="p-3">Policy</th>
            <th className="p-3">Expiry</th>
            <th className="p-3">Total</th>
            <th className="p-3">Status</th>
          </tr>
        </thead>
        <tbody>
          {quotes.map((quote) => (
            <tr
              className={[
                "cursor-pointer border-t",
                selectedId === quote.quotation_id ? "bg-primary/5" : "",
              ].join(" ")}
              data-testid={`commercial-quote-row-${quote.quotation_id}`}
              key={quote.quotation_id}
              onClick={() => onSelect(quote.quotation_id)}
            >
              <td className="p-3 font-mono text-xs">{quote.quotation_number}</td>
              <td className="p-3">
                <strong>{quote.society_name}</strong>
                <br />
                <span className="text-xs text-muted-foreground">
                  {quote.customer_contact?.name}
                </span>
              </td>
              <td className="p-3 text-xs">{quote.pricing_snapshot?.policy_version}</td>
              <td className="p-3">{quote.expires_on}</td>
              <td className="p-3 font-semibold">{moneyFromQuote(quote)}</td>
              <td className="p-3"><Status value={quote.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function QuoteDetail({
  acceptance,
  canAccept,
  canApprove,
  canEdit,
  onAccept,
  onAcceptanceChange,
  onApprove,
  onIssue,
  onReasonChange,
  onReject,
  onSupersede,
  quote,
  reason,
  saving,
  timeline,
  user,
}) {
  if (!quote) {
    return (
      <aside className="border bg-card p-6" data-testid="commercial-quote-detail-empty">
        <EmptyState icon={ScrollText} title="Select a quotation to review its terms and timeline" />
      </aside>
    );
  }
  const isCreator = quote.created_by?.user_id === user?.user_id;
  const canTransition = ["DRAFT", "ISSUED", "APPROVED"].includes(quote.status);
  return (
    <aside className="border bg-card p-5" data-testid="commercial-quote-detail">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-mono text-xs text-muted-foreground">{quote.quotation_number}</p>
          <h2 className="mt-1 font-display text-2xl font-black">{quote.society_name}</h2>
          <p className="mt-1 text-sm text-muted-foreground">Expires {quote.expires_on}</p>
        </div>
        <Status value={quote.status} />
      </div>

      <QuoteSnapshot
        quote={quote.pricing_snapshot?.calculation}
        testId="commercial-quote-detail-pricing"
        title="Frozen pricing snapshot"
      />

      {canTransition && (canEdit || canApprove || canAccept) && (
        <section className="mt-5 border-t pt-5" data-testid="commercial-quote-actions">
          <h3 className="font-semibold">Controlled lifecycle</h3>
          {quote.status === "DRAFT" && canEdit && (
            <Button
              className="mt-3 w-full"
              data-testid="commercial-quote-issue-button"
              disabled={saving}
              onClick={onIssue}
              type="button"
            >
              <Send className="h-4 w-4" /> Issue quotation
            </Button>
          )}
          {quote.status === "ISSUED" && canApprove && (
            <div className="mt-3 space-y-3">
              {isCreator && user?.role === "super_admin" && (
                <Input
                  data-testid="commercial-quote-override-reason-input"
                  onChange={(event) => onReasonChange(event.target.value)}
                  placeholder="Required Super Admin self-approval reason"
                  value={reason}
                />
              )}
              <Button
                className="w-full"
                data-testid="commercial-quote-approve-button"
                disabled={saving}
                onClick={onApprove}
                type="button"
              >
                <ShieldCheck className="h-4 w-4" /> Approve quotation
              </Button>
            </div>
          )}
          {quote.status === "APPROVED" && canAccept && (
            <AcceptanceForm
              acceptance={acceptance}
              onAccept={onAccept}
              onChange={onAcceptanceChange}
              saving={saving}
            />
          )}
          {(
            quote.status === "DRAFT"
            || quote.status === "ISSUED"
            || quote.status === "APPROVED"
          ) && canEdit && (
            <div className="mt-4 space-y-3">
              <Input
                data-testid="commercial-quote-reason-input"
                onChange={(event) => onReasonChange(event.target.value)}
                placeholder="Reason required for rejection or supersession"
                value={reason}
              />
              <div className="grid grid-cols-2 gap-3">
                <Button
                  data-testid="commercial-quote-reject-button"
                  disabled={saving || !reason.trim()}
                  onClick={onReject}
                  type="button"
                  variant="outline"
                >
                  <XCircle className="h-4 w-4" /> Reject
                </Button>
                <Button
                  data-testid="commercial-quote-supersede-button"
                  disabled={saving || !reason.trim()}
                  onClick={onSupersede}
                  type="button"
                  variant="outline"
                >
                  Supersede
                </Button>
              </div>
            </div>
          )}
        </section>
      )}

      <Timeline events={timeline} />
    </aside>
  );
}

function AcceptanceForm({ acceptance, onAccept, onChange, saving }) {
  const values = acceptance || {};
  const set = (key, value) => onChange({ ...values, [key]: value });
  return (
    <div className="mt-4 space-y-3" data-testid="commercial-quote-acceptance-form">
      <p className="text-xs text-muted-foreground">
        {"Recording acceptance creates an AWAITING_INVOICE contract only. " +
          "No invoice, receipt, payment or subscription is created in B1."}
      </p>
      <Input
        data-testid="commercial-quote-acceptance-name-input"
        onChange={(event) => set("accepted_by_name", event.target.value)}
        placeholder="Customer name accepting"
        value={values.accepted_by_name || ""}
      />
      <Input
        data-testid="commercial-quote-acceptance-method-input"
        onChange={(event) => set("method", event.target.value)}
        placeholder="Acceptance method"
        value={values.method || ""}
      />
      <Input
        data-testid="commercial-quote-acceptance-reference-input"
        onChange={(event) => set("reference", event.target.value)}
        placeholder="Acceptance reference"
        value={values.reference || ""}
      />
      <Input
        data-testid="commercial-quote-acceptance-note-input"
        onChange={(event) => set("note", event.target.value)}
        placeholder="Acceptance note"
        value={values.note || ""}
      />
      <Button
        className="w-full"
        data-testid="commercial-quote-accept-button"
        disabled={saving || !values.accepted_by_name || !values.method}
        onClick={onAccept}
        type="button"
      >
        <CheckCircle2 className="h-4 w-4" /> Record customer acceptance
      </Button>
    </div>
  );
}

function QuoteSnapshot({ quote, testId, title }) {
  if (!quote) {
    return (
      <aside className="border bg-muted/20 p-6" data-testid={`${testId}-empty`}>
        <FileClock className="h-6 w-6 text-muted-foreground" />
        <p className="mt-3 text-sm text-muted-foreground">
          Preview active policy pricing before creating a draft.
        </p>
      </aside>
    );
  }
  if (quote.status === "custom_quote") {
    return (
      <aside className="mt-5 border border-orange-200 bg-orange-50 p-5" data-testid={testId}>
        <h3 className="font-semibold">{title}</h3>
        <p className="mt-2 text-sm">{quote.message}</p>
      </aside>
    );
  }
  return (
    <aside className="mt-5 border bg-muted/20 p-5" data-testid={testId}>
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-semibold">{title}</h3>
        <span className="text-xs font-bold text-primary">
          {quote.policy?.policy_version}
        </span>
      </div>
      <div className="mt-4 space-y-2 text-sm">
        <SnapshotRow
          label="ERP subscription"
          value={quote.erp?.duration_subtotal_paise}
        />
        <SnapshotRow
          label={`ERP saving (${quote.erp?.discount_pct || 0}%)`}
          negative
          value={quote.erp?.discount_paise}
        />
        {quote.resident_portal_selected && (
          <SnapshotRow
            label="Resident Portal"
            value={quote.resident_portal?.duration_subtotal_paise}
          />
        )}
        <SnapshotRow
          label="Standard onboarding"
          value={quote.onboarding?.standard_fee_paise}
        />
        <SnapshotRow
          label={`Onboarding saving (${quote.onboarding?.discount_pct || 0}%)`}
          negative
          value={quote.onboarding?.discount_paise}
        />
        <SnapshotRow
          label={quote.gst?.enabled ? `GST (${quote.gst.rate_pct}%)` : "GST not currently applied"}
          value={quote.gst?.amount_paise}
        />
      </div>
      <div className="mt-4 border-t pt-4" data-testid={`${testId}-total`}>
        <div className="flex items-center justify-between gap-3 font-display text-lg font-black">
          <span>Indicative total</span>
          <span>{fromPaise(quote.final_payable_paise)}</span>
        </div>
        <p className="mt-1 text-xs text-emerald-700">
          Total savings {fromPaise(quote.total_savings_paise)}
        </p>
      </div>
    </aside>
  );
}

function ContractsTable({ canInvoice, contracts, onGenerateInvoice, saving }) {
  if (!contracts.length) {
    return <EmptyState icon={FileClock} title="No accepted commercial contracts yet" />;
  }
  return (
    <section className="mt-5 grid gap-5 xl:grid-cols-2" data-testid="commercial-contracts-table">
      {contracts.map((contract) => (
        <article
          className="border bg-card p-5"
          data-testid={`commercial-contract-row-${contract.contract_id}`}
          key={contract.contract_id}
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-mono text-xs text-muted-foreground">{contract.contract_id}</p>
              <h3 className="mt-1 font-display text-xl font-black">{contract.society_name}</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {contract.quotation_number} · {contract.pricing_snapshot?.policy_version}
              </p>
            </div>
            <Status value={contract.status} />
          </div>
          <QuoteSnapshot
            quote={contract.pricing_snapshot?.calculation}
            testId={`commercial-contract-snapshot-${contract.contract_id}`}
            title="Accepted quotation snapshot"
          />
          <div className="mt-5 grid gap-3 border-t pt-4 text-sm sm:grid-cols-2">
            <InvoiceDetail
              label="Onboarding entitlement"
              testId={`contract-entitlement-${contract.contract_id}`}
              value={contract.onboarding_entitlement?.status || "Pending invoice review"}
            />
            <InvoiceDetail
              label="Invoice source"
              testId={`contract-invoice-source-${contract.contract_id}`}
              value={contract.invoice_source || "Not posted"}
            />
            <InvoiceDetail
              label="Invoice number"
              testId={`contract-invoice-number-${contract.contract_id}`}
              value={
                contract.invoice?.invoice_number
                || contract.invoice_number
                || "Not generated"
              }
            />
            <InvoiceDetail
              label="Invoice status"
              testId={`contract-invoice-status-${contract.contract_id}`}
              value={contract.invoice?.status || "No Company Finance invoice"}
            />
            <InvoiceDetail
              label="Invoice total"
              testId={`contract-invoice-total-${contract.contract_id}`}
              value={contract.invoice ? inr(contract.invoice.total) : "—"}
            />
            <InvoiceDetail
              label="Reconciliation difference"
              testId={`contract-difference-${contract.contract_id}`}
              value={
                contract.invoice_difference_paise == null
                  ? "—"
                  : fromPaise(contract.invoice_difference_paise)
              }
            />
          </div>
          {contract.invoice_difference_paise != null && contract.invoice_difference_paise !== 0 && (
            <p
              className={[
                "mt-4 border border-rose-200 bg-rose-50 p-3",
                "text-sm font-medium text-rose-800",
              ].join(" ")}
              data-testid={`commercial-contract-difference-${contract.contract_id}`}
            >
              {"Invoice total does not match the immutable accepted quotation. " +
                "Financial review is required."}
            </p>
          )}
          {contract.status === "AWAITING_INVOICE" && canInvoice && (
            <Button
              className="mt-5 w-full"
              data-testid={`commercial-contract-generate-invoice-${contract.contract_id}`}
              disabled={saving}
              onClick={() => onGenerateInvoice(contract.contract_id)}
              type="button"
            >
              <FilePlus2 className="h-4 w-4" /> Generate Company Finance invoice
            </Button>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            {"Generating an invoice records an unpaid receivable only. " +
              "It never marks paid or activates a subscription."}
          </p>
        </article>
      ))}
    </section>
  );
}

function InvoiceDetail({ label, testId, value }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 font-medium" data-testid={testId}>
        {String(value).replaceAll("_", " ")}
      </p>
    </div>
  );
}

function Timeline({ events }) {
  return (
    <section className="mt-6 border-t pt-5" data-testid="commercial-quote-timeline">
      <div className="flex items-center gap-2 font-semibold">
        <ScrollText className="h-4 w-4" /> Timeline
      </div>
      {!events.length && (
        <p className="mt-3 text-sm text-muted-foreground">No recorded events yet.</p>
      )}
      <div className="mt-3 space-y-3">
        {events.map((event) => (
          <div
            className="border-l-2 border-primary/40 pl-3"
            data-testid={`commercial-quote-timeline-${event.log_id}`}
            key={event.log_id}
          >
            <p className="text-sm font-medium">{event.action.replaceAll("_", " ")}</p>
            <p className="text-xs text-muted-foreground">
              {event.timestamp} · {event.user_email || "System"}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Field({ children, label }) {
  return (
    <label className="block text-sm font-medium">
      {label}
      {children}
    </label>
  );
}

function SnapshotRow({ label, negative = false, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className={negative ? "text-emerald-700" : ""}>
        {negative ? "−" : ""}
        {fromPaise(value)}
      </span>
    </div>
  );
}

function Status({ value }) {
  const tone = {
    ACCEPTED: "bg-emerald-100 text-emerald-800",
    APPROVED: "bg-blue-100 text-blue-800",
    AWAITING_INVOICE: "bg-amber-100 text-amber-800",
    DRAFT: "bg-slate-100 text-slate-700",
    EXPIRED: "bg-rose-100 text-rose-800",
    ISSUED: "bg-violet-100 text-violet-800",
    REJECTED: "bg-rose-100 text-rose-800",
    SUPERSEDED: "bg-slate-100 text-slate-700",
  }[value] || "bg-muted text-muted-foreground";
  return (
    <span className={`inline-flex px-2 py-1 text-xs font-bold ${tone}`}>
      {String(value).replaceAll("_", " ")}
    </span>
  );
}

function fromPaise(value) {
  return inr((Number(value) || 0) / 100);
}

function moneyFromQuote(quote) {
  return fromPaise(quote.pricing_snapshot?.calculation?.final_payable_paise);
}